import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid'; // Make sure to install uuid
import {
  createCollectionData,
  deleteCollectionData,
  getCollectionData,
  updateCollectionData,
} from '../../util/api';

const INVITATION_TABLE = 'Invitation_Data';
const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// The collection endpoint defaults to 10 documents per query, which silently hid
// every invitation past the tenth. The panel is meant to keep the full history,
// so it asks for an explicit page of records, newest first.
const INVITE_FETCH_OPTIONS = { limit: 500, sort: { createdAt: -1 } };
// An address can end up with several records (an expired invite followed by a
// re-invite). "active" wins over "pending", which wins over anything expired.
const INVITE_STATUS_RANK = { active: 3, pending: 2 };

const mostRelevantInvite = (invitations = []) =>
  invitations.reduce((best, invitation) => {
    if (!best) {
      return invitation;
    }
    const rank = INVITE_STATUS_RANK[invitation?.status] || 1;
    const bestRank = INVITE_STATUS_RANK[best?.status] || 1;
    return rank > bestRank ? invitation : best;
  }, null);



// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/InviteFamiliesPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/InviteFamiliesPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/InviteFamiliesPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/InviteFamiliesPage/FETCH_TRANSACTION_ERROR';


export const USER_EXISTS_REQUEST = 'app/InviteFamiliesPage/USER_EXISTS_REQUEST';
export const USER_EXISTS_SUCCESS = 'app/InviteFamiliesPage/USER_EXISTS_SUCCESS';
export const USER_EXISTS_ERROR = 'app/InviteFamiliesPage/USER_EXISTS_ERROR';

export const GET_ALL_USERS_REQUEST = 'app/InviteFamiliesPage/GET_ALL_USERS_REQUEST';
export const GET_ALL_USERS_SUCCESS = 'app/InviteFamiliesPage/GET_ALL_USERS_SUCCESS';
export const GET_ALL_USERS_ERROR = 'app/InviteFamiliesPage/GET_ALL_USERS_ERROR';

export const DELETE_INVITE_REQUEST = 'app/InviteFamiliesPage/DELETE_INVITE_REQUEST';
export const DELETE_INVITE_SUCCESS = 'app/InviteFamiliesPage/DELETE_INVITE_SUCCESS';
export const DELETE_INVITE_ERROR = 'app/InviteFamiliesPage/DELETE_INVITE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  getAllExistUser: [],
  monthlyTimeSlots: {
    // Example format:
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  alreadyExistsInprogress: false,
  getAllUserExistsInprogress: false,
  alreadyExistsError: null,
  deletingInviteId: null,
  deleteInviteError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: { uuid: 2 } }, { id: { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: { uuid: 2 } }, { id: { uuid: 1 } }]


export default function InviteFamiliesPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };

    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data?.data?.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }

    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case USER_EXISTS_REQUEST:
      return { ...state, alreadyExistsInprogress: true, alreadyExistsError: null };

    case USER_EXISTS_SUCCESS: {
      return { ...state, alreadyExistsInprogress: false, payload };
    }

    case USER_EXISTS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, alreadyExistsInprogress: false, alreadyExistsError: payload };

    case GET_ALL_USERS_REQUEST:
      return { ...state, getAllUserExistsInprogress: true, alreadyExistsError: null };

    case GET_ALL_USERS_SUCCESS: {
      return { ...state, getAllUserExistsInprogress: false, getAllExistUser: payload };
    }

    case GET_ALL_USERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, getAllUserExistsInprogress: false, alreadyExistsError: payload };

    case DELETE_INVITE_REQUEST:
      return { ...state, deletingInviteId: payload, deleteInviteError: null };

    case DELETE_INVITE_SUCCESS:
      return { ...state, deletingInviteId: null };

    case DELETE_INVITE_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, deletingInviteId: null, deleteInviteError: payload };


    default:
      return state;
  }
}
// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });

const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});

const fetchTransactionError = e => ({
  type: FETCH_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

const alreadyExistsRequest = () => ({ type: USER_EXISTS_REQUEST });

const alreadyExistsSuccess = response => ({
  type: USER_EXISTS_SUCCESS,
  payload: response,
});

const alreadyExistsError = e => ({
  type: USER_EXISTS_ERROR,
  error: true,
  payload: e,
});

const getAllUserRequest = () => ({ type: GET_ALL_USERS_REQUEST });

const getAllUserSuccess = Data => ({
  type: GET_ALL_USERS_SUCCESS,
  payload: Data,
});

const getAllUserError = e => ({
  type: GET_ALL_USERS_ERROR,
  error: true,
  payload: e,
});

const deleteInviteRequest = id => ({ type: DELETE_INVITE_REQUEST, payload: id });

const deleteInviteSuccess = () => ({ type: DELETE_INVITE_SUCCESS });

const deleteInviteError = e => ({
  type: DELETE_INVITE_ERROR,
  error: true,
  payload: e,
});


// ================ Thunks ================ //


export const applyInvitation = ({ values }) => (dispatch) => {
  dispatch(fetchTransactionRequest());

  const { email, selectedMembership, bypassBackgroundCheck, bypassPayment } = values.values;
  const { inviterId, inviterName, inviterEmail, isAdmin, baseURL, existingInviteId } = values;
  const expiresAt = new Date(Date.now() + INVITE_TTL_MS);

  const payload = {
    email: email,
    inviterId,
    inviterName,
    isUsed: false,
    expiresAt,
    createdAt: new Date(),
    acceptedAt: null,
    status: "pending",
    url: baseURL,
    selectedMembership: selectedMembership || null,
    bypassBackgroundCheck: !!bypassBackgroundCheck,
    bypassPayment: !!bypassPayment,
    inviterEmail: inviterEmail || null,
    isAdmin: !!isAdmin,
  };

  // Re-inviting an address whose earlier invite expired revives that record instead
  // of inserting a second one, so the panel keeps one row per invitee.
  const request = existingInviteId
    ? updateCollectionData({
      id: existingInviteId,
      tableName: INVITATION_TABLE,
      payload,
      fnCallback: false,
    })
    : createCollectionData({ payload });

  return request
    .then((response) => {
      dispatch(fetchTransactionSuccess(response));
      return response;
    })
    .catch((error) => {
      dispatch(fetchTransactionError(error));
    });
};

export const alreadyExists = (values) => (dispatch) => {
  dispatch(alreadyExistsRequest());
  const { email } = values.values;
  const query = { email };
  // Older data can hold more than one record per address, so all of them are read
  // and the most significant one decides whether a new invite is allowed.
  const options = { limit: 20, sort: { createdAt: -1 } };

  return getCollectionData({
    tableName: INVITATION_TABLE,
    query,
    options,
    fnCallback: false,
  })
    .then((response) => {
      dispatch(alreadyExistsSuccess(response));
      return mostRelevantInvite(response?.data || []) || [];
    })
    .catch((error) => {
      dispatch(alreadyExistsError(error));
    });
};

// export const getAllUser = (currentUserId) => (dispatch) => {
//   dispatch(getAllUserRequest());


//   const query = { inviterId: currentUserId };
//   const options = {};

//   return getCollectionData({
//     tableName: "Invitation_Data",
//     query,
//     options,
//     fnCallback: false,
//   })
//     .then((response) => {
//       const Data = response?.data;

//           const today = new Date();
//           const expiryDate = new Date(Data.expiresAt);

//           const isSameDate = expiryDate.toDateString() === today.toDateString();

//           if (isSameDate && Data.status === "pending") {
//              updateCollectionData({
//               id:_id,
//               tableName: "Invitation_Data",
//               payload: payload,
//               fnCallback: false
//             });
//           }
//       dispatch(getAllUserSuccess(Data));
//       return response?.data || [];
//     })
//     .catch((error) => {
//       dispatch(getAllUserError(error));
//     });
// };

export const getAllUser = (currentUserId) => async (dispatch) => {
  dispatch(getAllUserRequest());

  const query = { inviterId: currentUserId };

  try {
    const response = await getCollectionData({
      tableName: INVITATION_TABLE,
      query,
      options: INVITE_FETCH_OPTIONS,
      fnCallback: false,
    });
    const invitations = response?.data || [];
    const today = new Date();

    const updatedInvitations = await Promise.all(
      invitations.map(async (invitation) => {
        const expiryDate = new Date(invitation.expiresAt);
        const isExpired = expiryDate < today && invitation.status === "pending";

        if (isExpired) {
          const payload = { status: "expired" };
          await updateCollectionData({
            id: invitation._id,
            tableName: INVITATION_TABLE,
            payload,
            fnCallback: false,
          });

          return { ...invitation, status: "expired" }; // Reflect updated status locally
        }

        return invitation;
      })
    );

    dispatch(getAllUserSuccess(updatedInvitations));
    return updatedInvitations;
  } catch (error) {
    dispatch(getAllUserError(error));
  }
};

// Removing an invite is the only way a record leaves the panel, so the list is
// refetched afterwards to keep it in step with the collection.
export const deleteInvite = (id, inviterId) => async (dispatch) => {
  dispatch(deleteInviteRequest(id));
  try {
    await deleteCollectionData(id);
    dispatch(deleteInviteSuccess());
    return dispatch(getAllUser(inviterId));
  } catch (error) {
    dispatch(deleteInviteError(error));
  }
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const currentUserId = params?.id || "";
  return Promise.all([
    dispatch(getAllUser(currentUserId))
  ]);
};
